import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Select,
    Stack
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { useCustomToast } from "components/CustomToast/CustomToast";
import Loading from "components/Loading/Loading";
import { ROUTES } from "constants/routes-path";
import { checkErrors, hasError, validateYearFormat } from "utils/forms-utils";
import { DocumentPermitteeDetranServiceType } from "constants/documents";
import { permitteesService } from "services/permitteesService";
import { documentsPermitteesService } from "services/documentsPermitteesService";
import PermitteesDocumentsListPageCardHeader from "./PermitteesDocumentsListPageCardHeader";
import { formatDocument, validateCpfOrCnpj } from "utils/utils";
import YearMaskedInput from "../../../../components/YearInput/YearMaskedInput";

function PermitteesDocumentsSendDetran() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const showToast = useCustomToast();

    const location = useLocation();
    const { id } = location.state || {};
    const [searchParams] = useSearchParams();
    const parentId = searchParams.get("parent_id");
    const [formData, setFormData] = useState(undefined);
    const [permitteeData, setPermitteeData] = useState(undefined);
    const documentPermitteeDetranServiceTypes = useMemo(() => Object.values(DocumentPermitteeDetranServiceType), []);
    const [inputError, setInputError] = useState({});

    const ids = useMemo(() => ({
        serviceType: "type",
        exerciseYear: "exercise-year",
        taximeter: "taximeter",
        tachograph: "tachograph",
        tourismRegistration: "turismo-registration",
        busCpfCnpj: "bus-cpf-cnpj",
        busOrder: "bus-order",
    }), []);

    const cardSpacing = "20px";
    const formSpacing = "30px";
    const formDirection = { sm: "column", md: "row" };

    const [touched, setTouched] = useState({});
    const handleBlur = (e) => {
        const id = e.target.id;
        setTouched((prev) => ({ ...prev, [id]: true }));
    };

    const isFormInvalid =
        !formData ||
        (permitteeData?.license_year_required && validateYearFormat(formData[ids.exerciseYear]?.value)) ||
        (permitteeData?.bus && !validateCpfOrCnpj(formData[ids.busCpfCnpj]?.value)) ||
        hasError({ formData, inputError });

    const initFormData = () => {
        return {
            [ids.serviceType]: {
                value: "",
                validations: [{ required: true }],
            },
            [ids.exerciseYear]: {
                value: "",
                validations: [{ required: !!permitteeData?.license_year_required }]
            },
            [ids.taximeter]: {
                value: "",
                validations: [],
            },
            [ids.tachograph]: {
                value: "",
                validations: [],
            },
            [ids.tourismRegistration]: {
                value: "",
                validations: [{ required: permitteeData.tourism }],
            },
            [ids.busCpfCnpj]: {
                value: "",
                validations: [
                    { required: permitteeData?.bus },
                    { custom: (value) => permitteeData.bus || validateCpfOrCnpj(value) }
                ],
            },
            [ids.busOrder]: {
                value: "",
                validations: [],
            },
        };
    };

    const toFormData = () => {
        const selectedType = documentPermitteeDetranServiceTypes.find(
            (type) => type.value === formData[ids.serviceType].value
        );

        return {
            tipo_servico: selectedType?.id ?? null,
            exercicio: formData[ids.exerciseYear].value,
            taximetro: formData[ids.taximeter].value || null,
            tacografo: formData[ids.tachograph].value || null,
            registro_ministerio_turismo: formData[ids.tourismRegistration]?.value || null,
            cpf_cnpj_onibus: formData[ids.busCpfCnpj]?.value || null,
            ordem_onibus: formData[ids.busOrder]?.value || null,
        };
    };

    const handleSubmit = async (e) => {
        if (isFormInvalid) return;

        try {
            const data = toFormData();
            await documentsPermitteesService.sendToDetran(data, id);
            showToast({ title: t("permittees.messages.success.send-detran"), status: "success" });
            navigate(`${ROUTES.permitteeDocumentsPath}/?parent_id=${parentId}`);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const handleInputChange = (e) => {
        const id = e.target.id;
        let value = e.target.value;

        if (id === ids.exerciseYear) {
            value = value.replace(/\D/g, '').slice(0, 4);
        }

        if (id === ids.busCpfCnpj) {
            value = formatDocument(value);
        }

        const newFormData = { ...formData, [id]: { ...formData[id], value } };
        const newErrors = checkErrors({ formData: newFormData, inputError, name: id });

        if (id === ids.exerciseYear && value.length > 0 && value.length < 4) {
            newErrors[id] = "permittees.messages.errors.year-invalid";
        }

        if (id === ids.busCpfCnpj && permitteeData?.bus && value.length > 0 && !validateCpfOrCnpj(value)) {
            newErrors[id] = "permittees.messages.errors.bus-cpf-cnpj";
        } else if (id === ids.busCpfCnpj && newErrors[id] === "permittees.messages.errors.bus-cpf-cnpj" && validateCpfOrCnpj(value)) {
            delete newErrors[id];
        }

        setFormData(newFormData);
        setInputError(newErrors);
    };

    const fetchPermitteeData = async () => {
        if (!parentId) return;

        try {
            const data = await permitteesService.detail(parentId);
            setPermitteeData(data);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    useEffect(() => {
        fetchPermitteeData();
    }, []);

    useEffect(() => {
        if (permitteeData) {
            setFormData(initFormData());
        }
    }, [permitteeData]);

    return (
        <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "75px" }}>
            <Card mt={"80px"}>
                {permitteeData && (
                    <PermitteesDocumentsListPageCardHeader permitteeData={permitteeData} />
                )}
                <ListPageCardHeader title={t("permittees.documents.send-detran.form.title")} />
                <CardBody px="22px">
                    {formData ? (
                        <Stack w="100%" direction="column" spacing={cardSpacing}>
                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isRequired isInvalid={!!inputError[ids.serviceType]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("permittees.documents.send-detran.form.fields.service-type.label")}
                                    </FormLabel>
                                    <Select
                                        fontSize="sm"
                                        id={ids.serviceType}
                                        value={formData[ids.serviceType].value}
                                        onChange={handleInputChange}
                                        placeholder={t("permittees.documents.send-detran.form.fields.service-type.placeholder")}
                                    >
                                        {documentPermitteeDetranServiceTypes.map((type) => (
                                            <option key={type.value} value={type.value}>
                                                {(type.labelKey)}
                                            </option>
                                        ))}
                                    </Select>
                                    <FormErrorMessage>
                                        {t(inputError[ids.serviceType])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>

                            {permitteeData.license_year_required &&
                                <Stack spacing={formSpacing} direction={formDirection}>
                                    <YearMaskedInput
                                        id={ids.exerciseYear}
                                        label={"permittees.documents.send-detran.form.fields.exercise-year.label"}
                                        placeholder={"permittees.documents.send-detran.form.fields.exercise-year.placeholder"}
                                        value={formData[ids.exerciseYear].value}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        error={inputError[ids.exerciseYear]}
                                        isRequired={permitteeData?.license_year_required}
                                        isInvalid={touched[ids.exerciseYear] && !!inputError[ids.exerciseYear]}
                                    />
                                </Stack>
                            }

                            {permitteeData.cab &&
                                <Stack spacing={formSpacing} direction={formDirection}>
                                    <FormControl isInvalid={inputError[ids.taximeter]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t("permittees.documents.send-detran.form.fields.taximeter.label")}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.taximeter}
                                            value={formData[ids.taximeter].value}
                                            onChange={handleInputChange}
                                            placeholder={t("permittees.documents.send-detran.form.fields.taximeter.placeholder")}
                                        />
                                    </FormControl>
                                </Stack>
                            }

                            {permitteeData.tourism && (
                                <Stack spacing={formSpacing} direction={formDirection}>
                                    <FormControl isRequired isInvalid={inputError[ids.tourismRegistration]}>
                                        <FormLabel fontSize="xs" fontWeight="bold">
                                            {t("permittees.documents.send-detran.form.fields.tourism-registration.label")}
                                        </FormLabel>
                                        <Input
                                            fontSize="sm"
                                            id={ids.tourismRegistration}
                                            value={formData[ids.tourismRegistration].value}
                                            onChange={handleInputChange}
                                            placeholder={t("permittees.documents.send-detran.form.fields.tourism-registration.placeholder")}
                                            maxLength={15}
                                        />
                                        <FormErrorMessage>
                                            {t(inputError[ids.tourismRegistration])}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Stack>
                            )}

                            {permitteeData.bus && (
                                <>
                                    <Stack spacing={formSpacing} direction={formDirection}>
                                        <FormControl
                                            isRequired
                                            isInvalid={touched[ids.busCpfCnpj] && !!inputError[ids.busCpfCnpj]}
                                        >
                                            <FormLabel fontSize="xs" fontWeight="bold">
                                                {t("permittees.documents.send-detran.form.fields.bus-cpf-cnpj.label")}
                                            </FormLabel>
                                            <Input
                                                fontSize="sm"
                                                id={ids.busCpfCnpj}
                                                maxLength="18"
                                                value={formData[ids.busCpfCnpj].value}
                                                onChange={handleInputChange}
                                                onBlur={handleBlur}
                                                placeholder={t("permittees.documents.send-detran.form.fields.bus-cpf-cnpj.placeholder")}
                                            />
                                            <FormErrorMessage>
                                                {t(inputError[ids.busCpfCnpj])}
                                            </FormErrorMessage>
                                        </FormControl>
                                    </Stack>

                                    <Stack spacing={formSpacing} direction={formDirection}>
                                        <FormControl isInvalid={inputError[ids.busOrder]}>
                                            <FormLabel fontSize="xs" fontWeight="bold">
                                                {t("permittees.documents.send-detran.form.fields.bus-order.label")}
                                            </FormLabel>
                                            <Input
                                                fontSize="sm"
                                                id={ids.busOrder}
                                                type="number"
                                                value={formData[ids.busOrder].value}
                                                onChange={handleInputChange}
                                                placeholder={t("permittees.documents.send-detran.form.fields.bus-order.placeholder")}
                                                max={999999}
                                            />
                                            <FormErrorMessage>
                                                {t(inputError[ids.busOrder])}
                                            </FormErrorMessage>
                                        </FormControl>
                                    </Stack>
                                </>
                            )}

                            <Stack spacing={formSpacing} direction={formDirection}>
                                <FormControl isInvalid={inputError[ids.tachograph]}>
                                    <FormLabel fontSize="xs" fontWeight="bold">
                                        {t("permittees.documents.send-detran.form.fields.tachograph.label")}
                                    </FormLabel>
                                    <Input
                                        fontSize="sm"
                                        id={ids.tachograph}
                                        value={formData[ids.tachograph].value}
                                        onChange={handleInputChange}
                                        placeholder={t("permittees.documents.send-detran.form.fields.tachograph.placeholder")}
                                    />
                                </FormControl>
                            </Stack>

                            <Flex w="100%" justify="space-between">
                                <Button
                                    variant="no-effects"
                                    alignSelf="flex-end"
                                    w="125px"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    fontWeight="bold"
                                    onClick={() => navigate(`${ROUTES.permitteeDocumentsPath}/?parent_id=${parentId}`, {
                                        state: { parentId }
                                    })}
                                >
                                    {t("system.actions.cancel")}
                                </Button>
                                <Button
                                    w="100px"
                                    variant="dark"
                                    fontSize="sm"
                                    onClick={handleSubmit}
                                    _hover={{
                                        bg: "gray.500",
                                        color: "white"
                                    }}
                                    isDisabled={isFormInvalid}
                                >
                                    {t("system.actions.save")}
                                </Button>
                            </Flex>
                        </Stack>
                    ) : (
                        <Loading h="300px" w="100%" />
                    )}
                </CardBody>
            </Card>
        </Flex >
    );
};

export default PermitteesDocumentsSendDetran;
